import type { Lesson } from '@yuan-math/curriculum';
import { figure, insight, lesson, list, math, practice, steps, table, text, tip, warning } from './authoring.ts';

/**
 * Proportional reasoning in use.
 *
 * Grade 6 established that a ratio compares by division. These lessons turn
 * that into an operator: something you *do* to a quantity, then a single
 * number that describes the whole relationship at once. Percent change is
 * where it earns its keep, because it is the mathematics a student will still
 * be using at forty.
 */
export const PROPORTION_LESSONS: Lesson[] = [
  lesson({
    id: 'proportions-01',
    unitId: 'unit-proportional-reasoning',
    conceptId: 'proportions',
    title: 'Proportions',
    summary: 'Two ratios set equal — the single move behind scaling recipes, maps, models and prices.',
    objectives: [
      'Recognise when two ratios form a proportion',
      'Solve for a missing term using the scale factor',
      'Tell a proportional relationship from an additive one',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'The same relationship, a different size',
        blocks: [
          text('A recipe for 4 people needs 300 g of rice. You are cooking for 10. How much rice?'),
          text('A **proportion** is simply the statement that two ratios are equal. Writing it down is most of the work:'),
          math('\\frac{300}{4} = \\frac{x}{10}'),
          text('Everything in this lesson is about solving that, and about noticing when a situation is *not* one of these.'),
        ],
      },
      intuition: {
        title: 'How many times, not how many more',
        blocks: [
          text('There are two ways to compare 4 and 10. You can say 10 is *6 more than* 4, or that 10 is *2.5 times* 4. Only the second one is any use here, because the rice scales with the number of people multiplicatively.'),
          insight('To solve a proportion, find the number that takes you from one side to the other, then apply it to the other part. Nothing else is needed — no cross-multiplication, no formula.'),
          math('4 \\times 2.5 = 10 \\quad \\Longrightarrow \\quad 300 \\times 2.5 = 750\\text{ g}'),
          warning('The classic error is additive: "4 became 10, that is 6 more, so 300 becomes 306." It gives an answer that looks like arithmetic and is wildly wrong — 306 g of rice would feed the original four people with barely any extra.', 'The mistake to watch for'),
          text('There is a quick test for whether a relationship really is proportional: **when one quantity is zero, is the other zero too?** Ten people eat 750 g; zero people eat nothing. That is proportional. A taxi that charges £3 to get in and £2 a mile is not — at zero miles you still owe £3.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'tape-diagram', rows: [{ label: 'People', parts: 4 }, { label: 'Rice (100 g)', parts: 3 }], unitLabel: 'the recipe as written' },
            'Four people to three hundred-gram portions. Scaling multiplies every block in both rows by the same amount.',
          ),
          table(['People', 'Rice (g)', 'Rice ÷ people'], [
            ['4', '300', '75'],
            ['8', '600', '75'],
            ['10', '750', '75'],
            ['1', '75', '75'],
          ]),
          insight('The last column never changes. That constant — 75 g of rice per person — is what makes the relationship proportional, and it is exactly the unit rate from Grade 6.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A map has a scale where 3 cm represents 12 km. Two towns are 7 cm apart on the map. How far apart are they really?'),
          steps([
            { label: 'Write the proportion, keeping the units in the same places.', latex: '\\frac{3\\text{ cm}}{12\\text{ km}} = \\frac{7\\text{ cm}}{x\\text{ km}}' },
            { label: 'Find the scale factor between the two map distances.', latex: '7 \\div 3 = \\tfrac{7}{3}' },
            { label: 'Apply it to the real distance.', latex: '12 \\times \\tfrac{7}{3} = 28' },
            { label: 'Answer, with units.', latex: '28\\text{ km}' },
          ]),
          tip('Set the proportion up so the same kind of quantity is on the same level in both fractions — centimetres above, kilometres below, on both sides. Mixing them is the one setup error that survives all the way to the answer.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('proportions', 4)] },
      independent: { title: 'On your own', blocks: [practice('proportions', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('proportions', 3, 'Deciding which relationships are genuinely proportional.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A proportion says two ratios are equal.',
            'Solve it by finding the multiplier from one side to the other, then applying it.',
            'Proportional means "how many times", never "how many more".',
            'A proportional relationship passes through zero: no people, no rice.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'proportional-relationships-01',
    unitId: 'unit-proportional-reasoning',
    conceptId: 'proportional-relationships',
    title: 'Proportional Relationships',
    summary: 'A whole relationship described by one number — and the three places that number hides.',
    objectives: [
      'Decide from a table whether a relationship is proportional',
      'Find the constant of proportionality and write y = kx',
      'Recognise a proportional relationship from its graph',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'One number for the whole relationship',
        blocks: [
          text('Solving a proportion answers one question: this many people need that much rice. But the recipe does not change between questions. There is a single number sitting underneath every version of it — 75 grams per person — and once you have that number you never need to set up a proportion again.'),
          text('That number is called the **constant of proportionality**, and it is written *k*:'),
          math('y = kx'),
          text('This lesson is about finding *k*, and about the harder skill of noticing when there is no such number to find.'),
        ],
      },
      intuition: {
        title: 'The column that does not change',
        blocks: [
          text('Take any table of two quantities and add a third column: y ÷ x. If that column is the same all the way down, the relationship is proportional and the repeated value is *k*.'),
          table(['Hours worked (x)', 'Pay in £ (y)', 'y ÷ x'], [
            ['2', '27', '13.5'],
            ['5', '67.50', '13.5'],
            ['8', '108', '13.5'],
          ]),
          insight('k is the amount of y for one unit of x — £13.50 an hour. It is the unit rate from Grade 6, given a job to do.'),
          text('Now the same test on a relationship that fails it. A gym charges £20 to join and £6 a month:'),
          table(['Months (x)', 'Total cost (y)', 'y ÷ x'], [
            ['1', '26', '26'],
            ['2', '32', '16'],
            ['5', '50', '10'],
          ]),
          warning('The third column moves, so there is no single k. Taking one row — say the first — and declaring "k = 26, so y = 26x" predicts £130 for five months when the real cost is £50. One row is never enough evidence.', 'Checking only one row'),
          text('The joining fee is what breaks it. A proportional relationship has nothing charged once: at zero months you owe nothing, and doubling the months doubles the cost. The gym does neither.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'coordinate-grid',
              min: 0,
              max: 10,
              lines: [
                { slope: 2, intercept: 0, label: 'y = 2x' },
                { slope: 1, intercept: 4, label: 'y = x + 4', dashed: true },
              ],
            },
            'Both are straight. Only the solid one passes through the origin, and only the solid one is proportional.',
          ),
          insight('A proportional relationship graphs as a straight line **through (0, 0)**. Straightness alone is not enough — the dashed line is perfectly straight and is not proportional, because it starts at 4 rather than at nothing.'),
          text('The point (1, k) is worth finding on any proportional graph. Move one step right, and the height you climb *is* k. That climb is what Grade 8 will call the slope; it is the same number under a different name.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A printer produces 42 pages in 6 minutes at a steady rate. Write an equation for the number of pages *y* in *x* minutes, and use it to find how long 175 pages take.'),
          steps([
            { label: 'Check the relationship passes through zero — in no minutes it prints no pages.', latex: '(0, 0)' },
            { label: 'Divide y by x to find the constant.', latex: 'k = \\frac{42}{6} = 7' },
            { label: 'Write the equation. k is pages per minute.', latex: 'y = 7x' },
            { label: 'Substitute the 175 pages and solve.', latex: '175 = 7x \\implies x = 25\\text{ minutes}' },
          ]),
          tip('Say k out loud with its units — "seven pages per minute". A k that is upside down sounds absurd the moment you do ("seven minutes per page" for a printer that manages 42 pages in 6), and that is far quicker than checking the arithmetic.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('proportional-relationships', 4)] },
      independent: { title: 'On your own', blocks: [practice('proportional-relationships', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('proportional-relationships', 3, 'Telling proportional relationships from the ones that merely look like them.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A proportional relationship is y = kx, and k = y ÷ x.',
            'Test every row of the table, not one: k has to be the same throughout.',
            'The graph is a straight line through the origin. Straight is not enough.',
            'k is the amount of y for one unit of x — the unit rate, doing a job.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'percent-applications-01',
    unitId: 'unit-proportional-reasoning',
    conceptId: 'percent-applications',
    title: 'Percent Change and Interest',
    summary: 'Increase, decrease, tax, tip, discount and interest are one calculation, once you know which amount counts as the whole.',
    objectives: [
      'Apply a percentage increase or decrease to an amount',
      'Find a percentage change from the original and the new amount',
      'Compute simple interest over several years',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'Six words for one calculation',
        blocks: [
          text('Discount. Tax. Tip. Markup. Interest. Depreciation. Six pieces of vocabulary, and behind them a single move: work out a percentage of an amount, then add it on or take it off.'),
          text('A £40 jacket reduced by 25%:'),
          math('\\frac{25}{100} \\times 40 = 10 \\qquad 40 - 10 = 30'),
          text('The whole topic is those two lines. What makes it worth a lesson is knowing which number to take the percentage *of*, and remembering which of the two lines the question actually asked for.'),
        ],
      },
      intuition: {
        title: 'Percent of what?',
        blocks: [
          text('A percentage is meaningless on its own. 20% is not an amount — it is an amount *of something*, and getting that something wrong is the error that matters here.'),
          insight('Percentage change is always measured against where you **started**. A price that goes from £50 to £60 has risen by £10, and £10 out of the original £50 is 20%.'),
          warning('Measure that same £10 against the £60 you ended up with and you get 16.7% — a real number, answering a question nobody asked. The original amount is the whole.', 'The wrong base'),
          text('There is a shortcut worth having, because it makes multi-step problems tractable. Instead of finding the change and then adding it, multiply once by a **multiplier**:'),
          table(['Change', 'Multiplier', 'Why'], [
            ['25% increase', '× 1.25', 'you keep 100% and gain 25%'],
            ['25% decrease', '× 0.75', 'you keep 100% − 25% = 75%'],
            ['8% tax added', '× 1.08', 'the bill plus 8% of it'],
            ['no change', '× 1.00', 'you keep all of it'],
          ]),
          insight('The multiplier also explains something that surprises people: a 10% rise followed by a 10% fall does not return you to where you started. 1.10 × 0.90 = 0.99, so you end up 1% down. The fall was of a bigger amount than the rise.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'bar-chart', bars: [{ label: 'Original', value: 40 }, { label: 'After −25%', value: 30 }], unitLabel: 'price in £' },
            'A quarter of the original bar is removed. The quarter is measured on the left-hand bar, never the right.',
          ),
          text('Simple interest is the same picture repeated. £500 at 4% a year earns £20 every year, on the original £500 each time — so after 3 years there is £60 of interest and £560 in the account.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A meal costs £64. A 15% service charge is added. What is the total bill, and what would it have been at a 10% charge?'),
          steps([
            { label: 'Find 15% of the bill.', latex: '\\frac{15}{100} \\times 64 = 9.60' },
            { label: 'Add it on — the question asks for the total, not the charge.', latex: '64 + 9.60 = 73.60' },
            { label: 'Or in one step, with the multiplier.', latex: '64 \\times 1.15 = 73.60' },
            { label: 'At 10%, the multiplier is 1.10.', latex: '64 \\times 1.10 = 70.40' },
          ]),
          tip('Read the last line of the question again before you write your answer. "How much is the discount?" and "what do you pay?" are different numbers, and the second is the one usually wanted.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('percent-applications', 4)] },
      independent: { title: 'On your own', blocks: [practice('percent-applications', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('percent-applications', 3, 'Working backwards from a change to the percentage, and interest over several years.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Percentage change is measured against the original amount, always.',
            'A multiplier does it in one step: +25% is × 1.25, −25% is × 0.75.',
            'Check whether the question wants the size of the change or the amount you end up with.',
            'Simple interest is earned on the original sum every year, so multiply one year by the number of years.',
          ]),
        ],
      },
    },
  }),
];
